import {
  build_case_form_sections,
  build_contact_rows,
  build_profile_rows,
  build_queue_items,
  build_stage_timeline,
  build_summary_rows,
  fetch_all_assigned_onboarding_cases,
  fetch_queue_onboarding_cases,
  hydrate_case_detail
} from '@/services/onboarding_journey_service';
import {
  build_created_onboarding_case_base,
  build_created_onboarding_template_name,
  build_selected_case_header_summary,
  get_created_onboarding_owner_role_name,
  get_created_onboarding_status
} from './my_queue_helpers';
import { build_compliance_panel_state } from '@/services/onboarding_compliance_service';
import {
  build_queue_form_route,
  normalize_created_onboarding_detail,
  open_queue_item_detail
} from './my_queue_queue_helpers';

function dispatch_case_scope_selected(case_item = {}) {
  window.dispatchEvent(new CustomEvent('onboarding-case-scope-selected', {
    detail: { case_item }
  }));
}

function dispatch_case_scope_cleared() {
  window.dispatchEvent(new CustomEvent('onboarding-case-scope-cleared'));
}

function dispatch_system_loader(message) {
  window.dispatchEvent(new CustomEvent('system-loader:show', {
    detail: message
  }));
}

function clear_system_loader() {
  window.dispatchEvent(new CustomEvent('system-loader:hide'));
}

function dispatch_sidebar_cases_loaded(cases = []) {
  window.dispatchEvent(new CustomEvent('onboarding-sidebar-cases-loaded', {
    detail: { cases }
  }));
}
function resolve_queue_cases(vm) {
  const cases = vm.is_exact_entity_role()
    ? fetch_all_assigned_onboarding_cases({ _: Date.now() })
    : fetch_queue_onboarding_cases();
  return cases;
}

function build_case_detail_state(selected_case, vm) {
  const agreement_rows = vm.build_case_agreement_rows(selected_case);
  const detail_state = {
    selected_case,
    summary_rows: build_summary_rows(selected_case),
    compliance_panel: build_compliance_panel_state(selected_case),
    compliance_error: '',
    profile_rows: build_profile_rows(selected_case),
    stage_timeline: build_stage_timeline(selected_case),
    case_form_sections: build_case_form_sections(selected_case),
    agreement_rows,
    detail_tabs: []
  };
  detail_state.detail_tabs = vm.build_detail_tabs(detail_state.case_form_sections, agreement_rows);
  return detail_state;
}

function build_case_detail_empty_state(selected_case, vm) {
  const detail_state = build_case_detail_state(selected_case, vm);
  detail_state.contact_rows = [];
  detail_state.evidence_items = [];
  return detail_state;
}

function build_case_detail_full_state(selected_case, vm) {
  const detail_state = build_case_detail_state(selected_case, vm);
  detail_state.contact_rows = build_contact_rows(selected_case);
  detail_state.evidence_items = [];
  return detail_state;
}

function build_created_onboarding_defaults(vm, detail, creation_response, selected_roles, selected_templates) {
  const defaults = {
    initial_workflow_status: vm.first_defined_value(get_created_onboarding_status(detail, creation_response), ''),
    owner_role_name: vm.first_defined_value(get_created_onboarding_owner_role_name(selected_roles), 'Unassigned'),
    template_name: build_created_onboarding_template_name(selected_templates)
  };
  return defaults;
}

function apply_case_detail_state(vm, detail_state) {
  Object.assign(vm, detail_state);
}

async function hydrate_selected_case(vm, case_item) {
  const hydrated_case = await hydrate_case_detail(case_item);
  const selected_case = vm.apply_persisted_edits_to_case(hydrated_case);
  const detail_state = build_case_detail_full_state(selected_case, vm);
  apply_case_detail_state(vm, detail_state);
}

export const myQueueQueueMethods = {
  is_exact_entity_role() {
    return String(this.$store.getters.currentRole || '').trim().toLowerCase() === 'entity';
  },
  selected_case_header_summary() {
    return build_selected_case_header_summary(this.selected_case);
  },
  async load_queue_items() {
    this.is_loading_queue = true;
    this.queue_error = '';
    dispatch_system_loader('Loading onboardings...');
    try {
      const cases = await resolve_queue_cases(this);
      this.queue_items = build_queue_items(cases).map((item) => this.apply_persisted_edits_to_queue_item(item));
      dispatch_sidebar_cases_loaded(cases);
    } catch (error) {
      this.queue_items = [];
      this.queue_error = error?.response?.data?.message || error?.message || 'Unable to load your queue.';
    } finally {
      this.is_loading_queue = false;
      this.has_loaded_queue_once = true;
      this.$nextTick(() => clear_system_loader());
    }
  },
  first_defined_value(values, fallback = '') {
    const value = values.find((candidate) => candidate !== undefined && candidate !== null && candidate !== '');
    return value === undefined ? fallback : value;
  },
  normalize_created_onboarding_journey(selected_entity_type) {
    return this.first_defined_value([
      selected_entity_type?.name,
      selected_entity_type?.entity_type_name,
      selected_entity_type?.entity_type?.entity_type_name
    ], 'Onboarding');
  },
  build_created_onboarding_case(detail = {}) {
    const normalized_detail = normalize_created_onboarding_detail(detail);
    const {
      creation_response,
      onboarding_form,
      selected_project,
      selected_entity_type,
      selected_roles,
      selected_templates
    } = normalized_detail;
    const onboarding_defaults = build_created_onboarding_defaults(this, detail, creation_response, selected_roles, selected_templates);
    const created_case = {
      ...build_created_onboarding_case_base({
        creation_response,
        onboarding_form,
        selected_project,
        selected_entity_type,
        onboarding_defaults
      }, this.first_defined_value, this.normalize_created_onboarding_journey),
      onboarding_template_names: selected_templates.map((template) => template?.name).filter(Boolean)
    };
    return created_case;
  },
  replace_or_prepend_queue_item(created_queue_item) {
    if (!created_queue_item?.case_item) return;
    const existing_index = this.queue_items.findIndex((item) => (
      String(item?.case_item?.application_id || '') === String(created_queue_item.case_item.application_id || '')
    ));
    if (existing_index >= 0) {
      const next_items = [...this.queue_items];
      next_items.splice(existing_index, 1);
      next_items.unshift(created_queue_item);
      this.queue_items = next_items;
      return;
    }
    this.queue_items = [created_queue_item, ...this.queue_items];
  },
  async prepend_created_onboarding_to_queue(detail = {}) {
    const created_case = this.build_created_onboarding_case(detail);
    try {
      const hydrated_case = await hydrate_case_detail(created_case);
      const hydrated_queue_item = this.apply_persisted_edits_to_queue_item(build_queue_items([hydrated_case])[0]);
      if (hydrated_queue_item?.case_item) {
        this.replace_or_prepend_queue_item(hydrated_queue_item);
        return;
      }
    } catch {
      // Fall back to the optimistic item if hydration fails; the regular queue refresh path will reconcile it later.
    }
    const optimistic_queue_item = this.apply_persisted_edits_to_queue_item(build_queue_items([created_case])[0]);
    if (!optimistic_queue_item?.case_item) return;
    this.replace_or_prepend_queue_item(optimistic_queue_item);
  },
  async handle_created_onboarding(event) {
    this.suppress_next_onboarding_data_update = true;
    await this.prepend_created_onboarding_to_queue(event?.detail || {});
  },
  handle_onboarding_data_updated() {
    if (this.suppress_next_onboarding_data_update) {
      this.suppress_next_onboarding_data_update = false;
      return;
    }
    this.load_queue_items();
  },
  async open_queue_item(item) {
    await open_queue_item_detail(this, item, {
      dispatch_case_scope_selected,
      dispatch_system_loader,
      clear_system_loader,
      build_case_detail_empty_state,
      hydrate_selected_case,
      apply_case_detail_state
    });
  },
  close_case_detail() {
    dispatch_case_scope_cleared();
    this.selected_case = null;
    this.compliance_panel = null;
    this.compliance_error = '';
    this.compliance_action_key = '';
    this.show_edit_modal = false;
    this.edit_error = '';
    this.detail_tab_overflow = { left: false, right: false };
  },
  async open_queue_form(item, event) {
    event?.stopPropagation?.();
    const case_item = item?.case_item;
    if (!case_item?.application_id) return;
    this.queue_action_key = `form:${case_item.application_id}`;
    dispatch_system_loader('Opening onboarding form...');
    dispatch_case_scope_selected(case_item);
    const route = build_queue_form_route(item, case_item, this.$route);
    try {
      await this.$router.push(route);
      dispatch_system_loader('Loading onboarding form...');
    } catch {
      this.queue_action_key = '';
      clear_system_loader();
    }
  }
};
